import { ClientContactPhone } from '../data/models/client.model';
import { PassengerPhone } from '../data/models/passenger.model';
import { ProviderPhoneContact } from '../data/models/provider.model';
import { TransportationProviderPhoneUse } from '../data/models/transportation-provider-phone-use.model';

class PhoneUtils {
  private format(
    strNum: string,
    withPrefix?: boolean,
    withCountryCode?: boolean,
  ): string {
    let replaceStr = `${withPrefix ? '+' : ''}${withCountryCode || withPrefix ? '$1 ' : ''}($2) $3-$4`;
    const num = strNum.replace(/(\d)(\d{3})(\d{3})(\d{4})/, replaceStr);
    return num || '';
  }

  public getPassengerHomePhone(
    phoneNumbers: PassengerPhone[],
  ): PassengerPhone | undefined {
    return phoneNumbers?.find((ph) => ph.phoneNumberTypeBC === 'LANDLINE');
  }

  public getPassengerMobilePhone(
    phoneNumbers: PassengerPhone[],
  ): PassengerPhone | undefined {
    return phoneNumbers?.find((ph) => ph.phoneNumberTypeBC === 'MOBILE');
  }

  public getPassengerNAPhone(
    phoneNumbers: PassengerPhone[],
  ): PassengerPhone | undefined {
    return phoneNumbers?.find((ph) => ph.phoneNumberTypeBC === 'NA');
  }

  public getClientLandlinePhone(
    phoneNumbers?: ClientContactPhone[],
  ): ClientContactPhone | undefined {
    if (phoneNumbers) {
      return phoneNumbers?.find((ph) => ph.phoneNumberTypeBC === 'LANDLINE');
    }
  }

  public getClientMobilePhone(
    phoneNumbers?: ClientContactPhone[],
  ): ClientContactPhone | undefined {
    if (phoneNumbers) {
      return phoneNumbers?.find((ph) => ph.phoneNumberTypeBC === 'MOBILE');
    }
  }

  public getProviderDispatchPhone(
    phoneNumbers?: ProviderPhoneContact[],
    providerPhoneUses?: TransportationProviderPhoneUse[],
  ): ProviderPhoneContact | undefined {
    if (phoneNumbers && providerPhoneUses) {
      return phoneNumbers?.find(
        (ph) =>
          ph.transportationProviderPhoneUseID ===
          providerPhoneUses?.find(
            (use) => use.transportationProviderPhoneUseBC === 'DISPATCH',
          )?.transportationProviderPhoneUseID,
      );
    }
  }

  /**
   * Formats a phone number given a PassengerPhone or ClientContactPhone object
   * @param strNum - the phone number to format
   * @param withPrefix - whether to include the country code prefix (+)
   * @param withCountryCode - whether to include the country code (354)
   */
  public formatPhone(
    phone?: PassengerPhone | ClientContactPhone | ProviderPhoneContact,
    withPrefix: boolean = true,
    withCountryCode: boolean = true,
  ): string {
    const strNum = phone?.phoneNumber ? phone.phoneNumber.substring(1) : '';
    return this.format(strNum, withPrefix, withCountryCode);
  }

  public getNumbersOnly(str: string) {
    const numbers = str.match(/\d+/g);
    return (numbers ? numbers.map(String) : []).join('');
  }

  /**
   * Formats a phone number given a string
   * @param phone - the phone number to format
   * @param withPrefix - whether to include the country code prefix (+)
   * @param withCountryCode - whether to include the country code (354)
   */
  public formatPhoneString(
    phone: string,
    withPrefix: boolean = true,
    withCountryCode: boolean = true,
  ): string {
    const nums = this.getNumbersOnly(phone);
    return this.format(nums, withPrefix, withCountryCode);
  }
}

const phoneUtils = new PhoneUtils();

/**
 * Trip reservation location phones use NANP (`+1`) via `mui-tel-input`. When the user has
 * not entered any subscriber digits, the control still holds a lone country code (e.g. `+1`).
 * APIs and XState machines should treat that as no number — same as an empty string.
 */
export function emptyStringIfOnlyNanpCallingCode(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) {
    return '';
  }
  if (digits === '1') {
    return '';
  }
  return value;
}

export { phoneUtils };
